import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import isInteger from 'lodash/isInteger'
import isNumber from 'lodash/isNumber'
import sumBy from 'lodash/sumBy'
import toNumber from 'lodash/toNumber'
import { useSearchParams } from 'react-router'
import { DocumentToolbar } from '@klear/ui/components/document-toolbar'
import { usePersistedState } from '@/app/documents/use-persisted-state'
import type { DocumentStatus } from '@klear/schema/documents'
import type { DocumentType } from '@klear/constants/document-types'
import { Tabs, TabsList, TabsTrigger } from '@klear/ui/components/tabs'
import { useAgentContext } from '@klear/ui/contexts/agent-context'
import { useDebouncedValue } from '@klear/ui/hooks/use-debounced-value'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { trpc } from '@klear/ui/lib/trpc'
import { PAGE_SIZES, TraderDocumentsTable } from '@/app/documents/trader-documents-table'

const PROCESSING_POLL_MS = 5000

const STATUS_TABS = [
  { id: 'all', label: 'All', statuses: [] },
  { id: 'processing', label: 'Processing', statuses: ['processing'] },
  { id: 'needs_review', label: 'Needs review', statuses: ['needs_review'] },
  { id: 'filed', label: 'Filed', statuses: ['filed'] },
  { id: 'dismissed', label: 'Dismissed', statuses: ['dismissed'] }
] as const satisfies ReadonlyArray<{ id: string; label: string; statuses: readonly DocumentStatus[] }>

type TabId = (typeof STATUS_TABS)[number]['id']

function tabOf(raw: unknown): TabId | undefined {
  return STATUS_TABS.find((tab) => tab.id === raw)?.id
}

type StartingFilters = { tab?: TabId; traderId?: number; search?: string }

function startingFilters(params: URLSearchParams): StartingFilters | undefined {
  const tab = tabOf(params.get('status'))
  const traderId = toNumber(params.get('trader'))
  const search = params.get('search') ?? ''
  const filters: StartingFilters = {
    ...(tab !== undefined && { tab }),
    ...(isInteger(traderId) && traderId > 0 && { traderId }),
    ...(search !== '' && { search })
  }
  return isEmpty(filters) ? undefined : filters
}

export default function Documents() {
  useDocumentTitle('Documents')
  const { traders } = useTraders()
  const [params, setParams] = useSearchParams()
  const [startWith] = useState(() => startingFilters(params))

  useEffect(() => {
    if (startWith !== undefined) setParams({}, { replace: true })
  }, [startWith, setParams])

  const [search, setSearch] = useState(startWith?.search ?? '')
  const [tab, setTab] = usePersistedState<TabId>(
    'documents.admin.tab.v2',
    'all',
    (raw) => tabOf(raw) ?? 'all',
    startWith === undefined ? undefined : { value: startWith.tab ?? 'all' }
  )
  const [traderId, setTraderId] = usePersistedState<number | undefined>(
    'documents.admin.traderId.v2',
    undefined,
    (raw) => (isNumber(raw) ? raw : undefined),
    startWith === undefined ? undefined : { value: startWith.traderId }
  )
  const [pageSize, setPageSize] = usePersistedState<number>('documents.admin.pageSize.v2', 25, (raw) =>
    isNumber(raw) && PAGE_SIZES.includes(raw) ? raw : 25
  )
  const [types, setTypes] = useState<DocumentType[]>([])
  const [page, setPage] = useState(0)
  const debouncedSearch = useDebouncedValue(search).trim()
  const statuses = STATUS_TABS.find((candidate) => candidate.id === tab)?.statuses ?? []
  const traderNameById = useMemo(() => new Map((traders ?? []).map((trader) => [trader.id, trader.name])), [traders])

  useAgentContext([
    'Documents (documents.list).',
    tab === 'all' ? '' : `${tab} tab.`,
    debouncedSearch === '' ? '' : `Search "${debouncedSearch}".`,
    traderId !== undefined ? `Trader ${traderId}.` : '',
    types.length > 0 ? `Types ${types.join(', ')}.` : '',
    page === 0 ? '' : `Page ${page + 1} (${pageSize}/page).`
  ])

  const listQuery = trpc.documents.list.useQuery(
    {
      ...(traderId !== undefined && { traderId }),
      ...(debouncedSearch !== '' && { search: debouncedSearch }),
      ...(statuses.length > 0 && { statuses: [...statuses] }),
      ...(types.length > 0 && { types }),
      limit: pageSize,
      offset: page * pageSize
    },
    {
      placeholderData: (previous) => previous,
      refetchInterval: (query) =>
        query.state.data?.rows.some(({ document }) => document.status === 'processing') === true
          ? PROCESSING_POLL_MS
          : false,
      refetchOnWindowFocus: true
    }
  )
  const counts = listQuery.data?.statusCounts
  const resetPage = () => setPage(0)
  const tabs = STATUS_TABS.map((candidate) => ({
    id: candidate.id,
    label: candidate.label,
    count:
      counts === undefined
        ? undefined
        : candidate.id === 'all'
          ? counts.all
          : sumBy(candidate.statuses, (status: DocumentStatus) => counts[status] ?? 0)
  }))

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-2xl font-semibold">Documents</h1>
      <Tabs
        value={tab}
        activationMode="manual"
        onValueChange={(value) => {
          const picked = tabOf(value)
          if (picked === undefined) return
          setTab(picked)
          resetPage()
        }}
      >
        <TabsList aria-label="Status" className="h-auto w-fit max-w-full justify-start overflow-x-auto">
          {tabs.map((candidate) => (
            <TabsTrigger key={candidate.id} value={candidate.id} className="h-7 gap-2 data-[state=active]:shadow-none">
              {candidate.label}
              {candidate.count !== undefined && (
                <span className="inline-grid h-5 min-w-5 place-items-center rounded-full bg-border px-1.5 text-xs tabular-nums">
                  {candidate.count}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <DocumentToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          resetPage()
        }}
        traderOptions={traders?.map((trader) => ({ id: trader.id, name: trader.name })) ?? []}
        selectedTraderId={traderId}
        onSelectTraderId={(id) => {
          setTraderId(id)
          resetPage()
        }}
        selectedTypes={types}
        onSelectTypes={(picked) => {
          setTypes(picked)
          resetPage()
        }}
      />
      <TraderDocumentsTable
        rows={listQuery.data?.rows ?? []}
        total={listQuery.data?.page.totalRows ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          resetPage()
        }}
        isLoading={listQuery.isPending}
        traderName={(id) => traderNameById.get(id)}
      />
    </div>
  )
}
